import React, { useState, useEffect, useCallback } from 'react';
import '../styles/pages/_group-list.scss';
import { getPublicGroups, discoverEnterpriseGroups, requestJoinGroup, GroupResponse } from '../logic/groupCreation/groupApi';
import { formatDate } from '../utils/dateFormatter';
import Modal from '../components/Modal';
import { useTheme } from '../contexts/ThemeContext';

// Loading Spinner Component
const LoadingSpinner: React.FC = () => (
  <div className="group-list__spinner">
    <div className="group-list__spinner-circle"></div>
  </div>
);

// API response interface
interface GroupsResponse {
  groups: GroupResponse[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalGroups: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const GroupListPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [groups, setGroups] = useState<GroupResponse[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<GroupResponse[]>([]);
  const [pagination, setPagination] = useState<GroupsResponse['pagination'] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState<{type: 'success' | 'error' | 'info', message: string} | null>(null);
  const [groupType, setGroupType] = useState<'consultant' | 'enterprise'>('enterprise');

  // Consent modal state
  const [selectedGroup, setSelectedGroup] = useState<GroupResponse | null>(null);
  const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmittingJoin, setIsSubmittingJoin] = useState(false);

  // Fetch groups from API
  const fetchGroups = useCallback(async (page: number = 1) => {
    try {
      setIsFetching(true);

      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          type: 'error',
          message: 'Authentication required. Please log in again.'
        });
        return;
      }

      let response;
      if (groupType === 'enterprise') {
        response = await discoverEnterpriseGroups(token, page, 12);
      } else {
        response = await getPublicGroups(token, page, 12);
      }

      setGroups(response.data.groups);
      setPagination(response.data.pagination);

      // Sync currentPage with API response if it differs
      if (response.data.pagination?.currentPage && response.data.pagination.currentPage !== currentPage) {
        setCurrentPage(response.data.pagination.currentPage);
      }
    } catch (error: any) {

      setNotification({
        type: 'error',
        message: error.message || 'Failed to load groups. Please try again.'
      });
      // Reset to empty array on error
      setGroups([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [groupType]);

  // Handle opening consent modal for join request
  const handleJoinRequest = (group: GroupResponse) => {
    setSelectedGroup(group);
    setIsConsentModalOpen(true);
    setConsentChecked(false);
  };

  // Handle actual join request submission with consent
  const handleJoinRequestSubmit = async () => {
    if (!selectedGroup || !consentChecked) return;

    setIsSubmittingJoin(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          type: 'error',
          message: 'Authentication required. Please log in again.'
        });
        return;
      }

      // Prepare consent payload
      const consentText = `By joining "${selectedGroup.groupName}", you authorize ${selectedGroup.consultantName || 'the group consultant'} to access your personal assessment answers, profile information (including age and other details), and consultation data for the purpose of providing personalized support and guidance within this platform.`;
      const consentPayload = {
        accepted: true,
        textVersion: 'join-group-consent-v1',
        text: consentText
      };

      await requestJoinGroup(token, selectedGroup.id, consentPayload);

      setNotification({
        type: 'success',
        message: 'Join request submitted successfully!'
      });

      // Close modal and reset state
      setIsConsentModalOpen(false);
      setSelectedGroup(null);
      setConsentChecked(false);

      // Refresh the groups list to update join status
      fetchGroups(currentPage);
    } catch (error: any) {

      setNotification({
        type: 'error',
        message: error.message || 'Failed to submit join request. Please try again.'
      });
    } finally {
      setIsSubmittingJoin(false);
    }
  };

  const totalItems = pagination?.totalGroups || 0;
  const totalPages = pagination?.totalPages || 1;

  // Load initial data
  useEffect(() => {
    fetchGroups(1);
  }, [fetchGroups]);

  // Handle page changes
  useEffect(() => {
    // Only fetch if we have a valid page number and are not in initial loading state
    if (currentPage >= 1) {
      fetchGroups(currentPage);
    }
  }, [currentPage, fetchGroups]);

  // Filter groups based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredGroups(groups);
    } else {
      const filtered = groups.filter(group =>
        group.groupName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (group.department && group.department.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredGroups(filtered);
    }
  }, [groups, searchTerm]);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePrevPage = () => {
    if (pagination?.hasPrev && currentPage > 1) {
      setCurrentPage(Math.max(1, currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className={`group-list ${isDarkMode ? 'dark' : ''}`}>
      <main className="group-list__main">
        <div className="group-list__container">
          {/* Header Section */}
          <div className="group-list__header">
            <div className="group-list__title-section">
              <h1 className="group-list__title">Group List</h1>
              <p className="group-list__subtitle">Browse and request to join groups that match your profile.</p>
            </div>
            {/* Filter Tabs */}
            <div className="group-list__filter-tabs">
              <button
                className={`group-list__filter-tab ${groupType === 'consultant' ? 'group-list__filter-tab--active' : ''}`}
                onClick={() => {
                  setGroupType('consultant');
                  setCurrentPage(1);
                }}
              >
                <span className="material-symbols-outlined">person</span>
                <span>Consultant Groups</span>
              </button>
              <button
                className={`group-list__filter-tab ${groupType === 'enterprise' ? 'group-list__filter-tab--active' : ''}`}
                onClick={() => {
                  setGroupType('enterprise');
                  setCurrentPage(1);
                }}
              >
                <span className="material-symbols-outlined">business</span>
                <span>Enterprise Groups</span>
              </button>
            </div>
          </div>

          {/* Notification */}
          {notification && (
            <div className={`group-list__notification group-list__notification--${notification.type}`}>
              <span className="group-list__notification-icon material-symbols-outlined">
                {notification.type === 'success' ? 'check_circle' :
                 notification.type === 'error' ? 'error' : 'info'}
              </span>
              <span className="group-list__notification-message">{notification.message}</span>
            </div>
          )}

          {/* Search Section */}
          <div className="group-list__search-section">
            <div className="group-list__search-input-wrapper">
              <span className="material-symbols-outlined group-list__search-icon">search</span>
              <input
                type="text"
                className="group-list__search-input"
                placeholder="Search groups by name, description, or department..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="group-list__search-clear"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              )}
            </div>
          </div>

          {/* Grid Section */}
          <div className="group-list__grid-container">
            {/* Header Info */}
            <div className="group-list__grid-header">
              <p className="group-list__grid-info">
                {searchTerm ? (
                  <>
                    Found {filteredGroups.length} of {groups.length} groups
                    {searchTerm && (
                      <span className="group-list__search-term"> for "{searchTerm}"</span>
                    )}
                  </>
                ) : (
                  <>
                    Showing page {currentPage} of {totalPages}
                  </>
                )}
              </p>
            </div>

            {/* Loading Overlay */}
            {isFetching && !isLoading && (
              <div className="group-list__loading-overlay">
                <LoadingSpinner />
                <span>Loading groups...</span>
              </div>
            )}

            {/* Grid */}
            {isLoading ? (
              <div className="group-list__loading">
                <LoadingSpinner />
                <span>Loading groups...</span>
              </div>
            ) : filteredGroups.length === 0 ? (
              <div className="group-list__empty">
                <div className="group-list__empty-icon">
                  <span className="material-symbols-outlined">groups</span>
                </div>
                <h3 className="group-list__empty-title">No Groups Found</h3>
                <p className="group-list__empty-description">
                  {searchTerm ? 'No groups match your search. Try a different search term.' : 'No groups available to join at this time.'}
                </p>
              </div>
            ) : (
              <div className="group-list__grid">
                {filteredGroups.map((group) => (
                  <div key={group.id} className="group-list__card">
                    <div className="group-list__card-header">
                      <div className="group-list__card-icon">
                        <span className="material-symbols-outlined">groups</span>
                      </div>
                      <span className="group-list__card-type">{group.program || 'Group'}</span>
                    </div>

                    <div className="group-list__card-content">
                      <h3 className="group-list__card-title">{group.groupName}</h3>

                      {/* Members count */}
                      <div className="group-list__card-meta">
                        <span className="material-symbols-outlined group-list__card-meta-icon">groups</span>
                        <span className="group-list__card-meta-text">{group.approvedMembers ?? 0} Members</span>
                      </div>

                      {/* Consultant or Enterprise name */}
                      {group.consultantName && (
                        <div className="group-list__card-meta">
                          <span className="material-symbols-outlined group-list__card-meta-icon">person</span>
                          <span className="group-list__card-meta-text">{group.consultantName}</span>
                        </div>
                      )}

                      {group.groupType === 'enterprise' && !group.consultantName && (
                        <div className="group-list__card-meta">
                          <span className="material-symbols-outlined group-list__card-meta-icon">business</span>
                          <span className="group-list__card-meta-text">Enterprise Group</span>
                        </div>
                      )}

                      {/* Description */}
                      <p className="group-list__card-description">{group.description || 'No description available'}</p>

                      {/* Additional stats */}
                      <div className="group-list__card-stats">
                        {group.department && (
                          <div className="group-list__card-stat">
                            <span className="material-symbols-outlined">business</span>
                            <span>{group.department}</span>
                          </div>
                        )}
                        <div className="group-list__card-stat">
                          <span className="material-symbols-outlined">calendar_today</span>
                          <span>{formatDate(group.createdAt)}</span>
                        </div>
                      </div>

                      {/* Breadcrumbs - folder path */}
                      {group.breadcrumbs && group.breadcrumbs.length > 0 && (
                        <div className="group-list__breadcrumbs">
                          {group.breadcrumbs.map((crumb, idx) => (
                            <React.Fragment key={idx}>
                              <span className="group-list__breadcrumb-item">{crumb}</span>
                              {idx < group.breadcrumbs!.length - 1 && (
                                <span className="group-list__breadcrumb-separator">/</span>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="group-list__card-footer">
                      {(group.joinStatus === 'none' || group.joinStatus === 'rejected') && (
                        <button
                          className="group-list__join-btn group-list__join-btn--default"
                          onClick={() => handleJoinRequest(group)}
                          disabled={isFetching}
                        >
                          <span className="material-symbols-outlined">check</span>
                          <span>Request to Join</span>
                        </button>
                      )}
                      {group.joinStatus === 'pending' && (
                        <span className="group-list__join-status group-list__join-status--pending">
                          <span className="material-symbols-outlined">schedule</span>
                          <span>Requested</span>
                        </span>
                      )}
                      {group.joinStatus === 'approved' && (
                        <span className="group-list__join-status group-list__join-status--approved">
                          <span className="material-symbols-outlined">check_circle</span>
                          <span>Approved</span>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {!searchTerm && filteredGroups.length > 0 && (
              <nav aria-label="Pagination" className="group-list__pagination">
                <ul className="group-list__pagination-list">
                  <li>
                    <button
                      className="group-list__pagination-btn"
                      onClick={handlePrevPage}
                      disabled={!pagination?.hasPrev}
                    >
                      <span className="material-symbols-outlined">chevron_left</span>
                      <span>Previous</span>
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, index) => index + 1).slice(0, 5).map((page) => (
                    <li key={page}>
                      <button
                        className={`group-list__pagination-btn ${
                          page === currentPage ? 'group-list__pagination-btn--active' : ''
                        }`}
                        onClick={() => handlePageChange(page)}
                        aria-current={page === currentPage ? 'page' : undefined}
                      >
                        {page}
                      </button>
                    </li>
                  ))}
                  {totalPages > 5 && (
                    <li>
                      <span className="group-list__pagination-ellipsis">...</span>
                    </li>
                  )}
                  <li>
                    <button
                      className="group-list__pagination-btn"
                      onClick={handleNextPage}
                      disabled={!pagination?.hasNext}
                    >
                      <span>Next</span>
                      <span className="material-symbols-outlined">chevron_right</span>
                    </button>
                  </li>
                </ul>
              </nav>
            )}
          </div>
        </div>
      </main>

      {/* Consent Modal */}
      <Modal
        isOpen={isConsentModalOpen}
        title="Consent to Share Your Data"
        onClose={() => {
          setIsConsentModalOpen(false);
          setSelectedGroup(null);
          setConsentChecked(false);
        }}
        actionLabel="I Agree & Request to Join"
        onAction={handleJoinRequestSubmit}
        actionDisabled={!consentChecked || isSubmittingJoin}
      >
        {selectedGroup && (
          <div className="join-group-consent-modal">
            {/* Group description first */}
            <div className="join-group-consent-modal__description">
              <h3 className="join-group-consent-modal__description-title">About This Group</h3>
              <p className="join-group-consent-modal__description-text">
                {selectedGroup.description || 'No description available'}
              </p>
            </div>

            {/* Group info */}
            <div className="join-group-consent-modal__group-info">
              <h3 className="join-group-consent-modal__group-name">{selectedGroup.groupName}</h3>
              <p className="join-group-consent-modal__consultant">
                Consultant: {selectedGroup.consultantName || 'Group Consultant'}
              </p>
              <p className="join-group-consent-modal__department">
                Department: {selectedGroup.department || 'N/A'}
              </p>
            </div>

            {/* Warnings and consent text */}
            <div className="join-group-consent-modal__consent-text">
              <p>
                By joining <strong>"{selectedGroup.groupName}"</strong>, you authorize{' '}
                <strong>{selectedGroup.consultantName || 'the group consultant'}</strong> to access:
              </p>
              <ul>
                <li>Your personal assessment answers and results</li>
                <li>Your profile information (including age and other details)</li>
                <li>Your consultation data and wellness metrics</li>
              </ul>
              <p>
                This access is provided for the purpose of delivering personalized support,
                guidance, and consultation services within this platform.
              </p>
            </div>

            {/* Consent checkbox */}
            <div className="join-group-consent-modal__checkbox-container">
              <label className="join-group-consent-modal__checkbox-label">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="join-group-consent-modal__checkbox"
                />
                <span className="join-group-consent-modal__checkbox-text">
                  I understand and consent to share my data with this consultant for this group.
                </span>
              </label>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GroupListPage;

